import React, { Component } from 'react';
import { useHistory } from 'react-router-dom';

import { useRouteConfiguration } from '../../../context/routeConfigurationContext';
import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { createResourceLocatorString } from '../../../util/routes';
import { getSearchPageResourceLocatorStringParams } from '../SearchPage.shared';

import MapRoundedIcon from '@mui/icons-material/MapRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import {
  Box,
  Button,
  ButtonGroup,
  Circle,
  Float,
  IconButton,
  Text,
  VStack,
} from '@chakra-ui/react';
import SearchFiltersSection from '../../../containers/SearchPage/SearchFiltersPopUp/SearchFiltersSection';

class SearchFiltersMobileComponent extends Component {
  constructor(props) {
    super(props);
    this.state = { isFiltersOpenOnMobile: false, initialQueryParams: null };

    this.openFilters = this.openFilters.bind(this);
    this.cancelFilters = this.cancelFilters.bind(this);
    this.closeFilters = this.closeFilters.bind(this);
    this.resetAll = this.resetAll.bind(this);
  }

  // Open filters modal, set the initial parameters to current ones
  openFilters() {
    const { onOpenModal, urlQueryParams } = this.props;
    onOpenModal();
    this.setState({ isFiltersOpenOnMobile: true, initialQueryParams: urlQueryParams });
  }

  // Close the filters by clicking cancel, revert to the initial params
  cancelFilters() {
    const { history, onCloseModal, location, routeConfiguration } = this.props;

    const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
      routeConfiguration,
      location
    );

    history.push(
      createResourceLocatorString(
        routeName,
        routeConfiguration,
        pathParams,
        this.state.initialQueryParams
      )
    );
    onCloseModal();
    this.setState({ isFiltersOpenOnMobile: false, initialQueryParams: null });
  }

  // Close the filter modal
  closeFilters() {
    this.props.onCloseModal();
    this.setState({ isFiltersOpenOnMobile: false });
  }

  // Reset all filter query parameters
  resetAll(e) {
    this.props.resetAll(e);

    // blur event target if event is passed
    if (e && e.currentTarget) {
      e.currentTarget.blur();
    }
  }

  render() {
    const {
      sortByComponent,
      listingsAreLoaded,
      resultsCount,
      searchInProgress = false,
      onMapIconClick = () => {},
      selectedFiltersCount = 0,
      isMapVariant = true,
      availableFilters,
    } = this.props;

    const resultsFound = (
      <FormattedMessage id="SearchFiltersMobile.foundResults" values={{ count: resultsCount }} />
    );
    const loadingResults = <FormattedMessage id="SearchFiltersMobile.loadingResults" />;

    const hasSelectedFilters = selectedFiltersCount > 0;

    return (
      <VStack
        p={'16px 0 6px 0'}
        display={{
          mdDown: 'flex',
          md: 'none',
        }}
        flexDirection={'column'}
        justify={'space-between'}
        bgColor={'white'}
        position={'sticky'}
        top={-1}
        zIndex={1}
        shadow={'lg'}
      >
        <Box>
          <ButtonGroup variant="outline" justify={'center'} align={'center'} w={'100%'}>
            {isMapVariant ? (
              <Button
                variant={'solid'}
                onClick={onMapIconClick}
                aria-label="Open map view"
                rounded="3xl"
                className={'plausible-event-name=Search-Page-Open-Map-View-Button'}
              >
                <MapRoundedIcon /> Map
              </Button>
            ) : null}
            <Button
              aria-label="Open filters"
              variant={'outline'}
              borderColor={hasSelectedFilters ? 'gray.900' : ''}
              borderWidth={hasSelectedFilters ? '0.15rem' : ''}
              onClick={this.openFilters}
              rounded="full"
              className={'plausible-event-name=Search+Page+Open+Filters+Button'}
            >
              {selectedFiltersCount > 0 && (
                <Float placement={'top-end'} borderColor={'white'} borderWidth={'0.125rem'}>
                  <Circle size="5" bg="almostBlack" color="white">
                    {selectedFiltersCount}
                  </Circle>
                </Float>
              )}
              <TuneRoundedIcon /> Filters
            </Button>
            {sortByComponent}
          </ButtonGroup>
          <Text textStyle="md" textAlign={'center'} fontWeight={'semibold'}>
            {listingsAreLoaded && resultsCount > 0 ? resultsFound : null}
            {searchInProgress ? loadingResults : null}
          </Text>
          <SearchFiltersSection
            totalItems={resultsCount}
            searchInProgress={searchInProgress}
            availableFilters={availableFilters}
            isModalOpenMobile={this.state.isFiltersOpenOnMobile}
            onClose={this.closeFilters}
            mobileView
          />
        </Box>
      </VStack>
    );
  }
}

/**
 * SearchFiltersMobile component
 *
 * @component
 * @param {Object} props
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {Object} props.urlQueryParams - The URL query params
 * @param {React.Node} props.sortByComponent - The sort by component
 * @param {boolean} props.listingsAreLoaded - Whether the listings are loaded
 * @param {number} props.resultsCount - The number of results
 * @param {boolean} props.searchInProgress - Whether the search is in progress
 * @param {number} props.showAsModalMaxWidth - The maximum width of the modal
 * @param {Function} props.onMapIconClick - The function to click the map icon
 * @param {Function} props.onManageDisableScrolling - The function to manage disable scrolling
 * @param {Function} props.onOpenModal - The function to open the modal
 * @param {Function} props.onCloseModal - The function to close the modal
 * @param {Function} props.resetAll - The function to reset all
 * @param {number} props.selectedFiltersCount - The number of selected filters
 * @param {boolean} props.isMapVariant - Whether the map variant is enabled
 * @returns {JSX.Element}
 */
const SearchFiltersMobile = props => {
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();

  return (
    <SearchFiltersMobileComponent
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      {...props}
    />
  );
};

export default SearchFiltersMobile;
